import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './PlaceOrder.module.css';
import { Alert } from '@material-ui/lab';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { setMinutes, setHours } from 'date-fns';

import { formatPhoneNumber } from '../../app/utils';
import { getPublicEstimate } from './orderGroupAPI';
import { saveGuestDraft } from './guestDraft';
import { InfoTooltip, LoadingOverlay, SELECTED_ADDRESS_ZOOM, StopSummaryTooltip, TealSwitch, useIsCompact, VehicleSelector } from './common';
import { toLocation, useAddressAutocomplete } from './addressAutocomplete';
import { buildPickUpSummary } from './stopSummary';
import { Topbar } from '../../components/topbar/Topbar';
import { GuestStop } from './GuestStop';
import { DateTimeField } from './DateTimeField';
import { StopDetailsPopover } from './StopDetailsPopover';
import { LoginModal } from '../login/LoginModal';
import { Map as SimpleMap } from '../../components/map/Map';
import { PublicEstimateMap } from './MapWithDirectons/PublicEstimateMap';

import plusIcon from "../../images/place_order/plus-icon.png";
import clockIcon from "../../images/place_order/clock-icon.png";
import halalIcon from "../../images/place_order/halal-icon.png";
import targetIcon from "../../images/place_order/target-icon.png";
import savedAddressIcon from "../../images/place_order/saved-address-icon.png";
import locationPinIcon from "../../images/place_order/location-pin-icon.png";
import closeIcon from "../../images/place_order/close-icon.png";

const GOOGLE_DIRECTION_WAYPOINT_LIMIIT = 25;

const emptyStop = () => ({
  dropOffLocation: {
    unitNumber: "",
    address: "",
    coordinate: {
      latitude: "",
      longitude: ""
    },
    city: "",
    country: ""
  },
  recipientName: "",
  recipientPhone: "",
  deliverDateTime: "",
  doorToDoor: false,
  isCodEnabled: false,
  codAmount: 0,
  deliveryFee: 0,
});

const coordinateKey = (location) => `${location.coordinate.latitude},${location.coordinate.longitude}`;

const SortableItem = SortableElement((props) => <GuestStop {...props} />);

const SortableList = SortableContainer(({ orders, editStopIndex, addStop, onAddressChange, onOpenDetails, onRemove, onMove, onError }) => (
  <div>
    {
      orders.map((stop, index) => (
        <SortableItem
          key={`stop-${index}`}
          index={index}
          currIdx={index}
          stop={stop}
          active={editStopIndex === index}
          showThread={index < orders.length - 1 || addStop}
          totalStops={orders.length}
          onAddressChange={onAddressChange}
          onOpenDetails={onOpenDetails}
          onRemove={onRemove}
          onMove={onMove}
          onError={onError}
        />
      ))
    }
  </div>
));

export function PublicEstimate() {
  const [pickUpForm, setPickUpForm] = useState({
    deliveryMethod: "MOTOR",
    halal: false,
    pickUpPersonName: "",
    pickUpContactNumber: "",
    pickUpDateTime: "",
    pickUpLocation: {
      unitNumber: "",
      address: "",
      coordinate: {
        latitude: "",
        longitude: ""
      },
      city: "",
      country: ""
    }
  });
  const [orders, setOrders] = useState([]);
  const [directions, setDirections] = useState([]);
  const [addStop, setAddStop] = useState(true);
  const [enterPickUpDetails, setEnterPickUpDetails] = useState(false);
  const [editStopIndex, setEditStopIndex] = useState("");
  const [currentLocation, setCurrentLocation] = useState({ lat: null, lng: null });
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [showPickUpInfoWindow, setShowPickUpInfoWindow] = useState(false);
  const [showDropOffInfoWindow, setShowDropOffInfoWindow] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sheetCollapsed, setSheetCollapsed] = useState(true);

  const isCompact = useIsCompact();
  const panelScrollRef = useRef(null);

  const { pickUpLocation } = pickUpForm;
  const { latitude: pickUpLat, longitude: pickUpLng } = pickUpLocation.coordinate;
  const hasPickUpCoordinate = pickUpLat !== "" && pickUpLng !== "";

  const raiseError = useCallback((show, message) => {
    setError(show);
    setErrorMessage(message);
  }, []);

  /* ---------- details dialog ---------- */

  const activeStopIndex = enterPickUpDetails
    ? 0
    : editStopIndex !== ""
      ? Number(editStopIndex) + 1
      : null;

  useLayoutEffect(() => {
    if (activeStopIndex === null) return;
    const row = document.querySelectorAll(`.${styles.poStop}`)[activeStopIndex];
    if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeStopIndex]);

  useEffect(() => {
    if (isCompact && activeStopIndex !== null) setSheetCollapsed(false);
  }, [isCompact, activeStopIndex]);

  const mapFocus = useMemo(
    () => hasPickUpCoordinate ? { lat: Number(pickUpLat), lng: Number(pickUpLng) } : null,
    [hasPickUpCoordinate, pickUpLat, pickUpLng]
  );

  /* ---------- addresses ---------- */

  const openPickUpDetails = () => {
    setEnterPickUpDetails(true);
    setEditStopIndex("");
  };

  const closeDetails = () => {
    setEnterPickUpDetails(false);
    setEditStopIndex("");
    raiseError(false, "");
  };

  useAddressAutocomplete("pickUpAutocomplete", (place) => {
    try {
      setPickUpForm(prevPickUpForm => ({
        ...prevPickUpForm,
        pickUpLocation: { ...prevPickUpForm.pickUpLocation, ...toLocation(place) }
      }));
      raiseError(false, "");
      openPickUpDetails();
    } catch (e) {
      raiseError(true, "Please select a pick-up address from the drop-down list.");
    }
  });

  useAddressAutocomplete(`dropOffAutocomplete-${orders.length}`, (place) => {
    try {
      const stop = emptyStop();
      setOrders(prevOrders => [...prevOrders, { ...stop, dropOffLocation: { ...stop.dropOffLocation, ...toLocation(place) } }]);
      setAddStop(false);
      setEnterPickUpDetails(false);
      setEditStopIndex(orders.length);
      raiseError(false, "");
    } catch (e) {
      raiseError(true, "Please select a drop-off address from the drop-down list.");
    }
  });

  const handleStopAddressChange = (index, location) => {
    setOrders(prevOrders => prevOrders.map((stop, i) =>
      i === index
        ? { ...stop, dropOffLocation: { ...stop.dropOffLocation, ...location } }
        : stop
    ));
  }

  const handleUseCurrentLocation = () => {
    if (!locationEnabled) {
      raiseError(true, "Please enable your location services to retrieve your current location.");
      return;
    }

    if (currentLocation.lat == null) {
      raiseError(true, "Retrieving your current location... Please try again after 10 seconds.");
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: currentLocation }, (results, status) => {
      if (status !== "OK" || !results[0]) {
        raiseError(true, "Error retrieving your current location.");
        return;
      }

      const place = { ...results[0], name: results[0].formatted_address };
      const location = { ...toLocation(place), address: results[0].formatted_address };

      document.getElementById("pickUpAutocomplete").value = results[0].formatted_address;

      setPickUpForm(prevPickUpForm => ({
        ...prevPickUpForm,
        pickUpLocation: { ...prevPickUpForm.pickUpLocation, ...location }
      }));

      raiseError(false, "");
      openPickUpDetails();
    });
  };

  useEffect(() => {
    const watchId = navigator.geolocation.watchPosition((position) => {
      setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
      setLocationEnabled(true);
    }, () => {
      setLocationEnabled(false);
    });

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  /* ---------- pricing ---------- */

  const getDirectionRoutes = useCallback(async (routeStops) => {
    if (routeStops.length === 0) {
      setDirections([]);
      return;
    }

    const DirectionsService = new window.google.maps.DirectionsService();
    const locations = [
      new window.google.maps.LatLng(pickUpLocation.coordinate.latitude, pickUpLocation.coordinate.longitude),
      ...routeStops.map(stop => new window.google.maps.LatLng(stop.dropOffLocation.coordinate.latitude, stop.dropOffLocation.coordinate.longitude))
    ];

    const waypointBlocks = [];
    for (let i = 0, max = GOOGLE_DIRECTION_WAYPOINT_LIMIIT - 1; i < locations.length; i = i + max) {
      waypointBlocks.push(locations.slice(i, i + max + 1));
    }

    const finalDirectionResults = [];
    for (let i = 0; i < waypointBlocks.length; i++) {
      const block = waypointBlocks[i];
      const waypoints = block.slice(1, block.length - 1).map(location => ({ location, stopover: true }));

      await DirectionsService.route({
        origin: block[0],
        destination: block[block.length - 1],
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING
      }, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          finalDirectionResults.push(result);
        } else {
          console.error(`error fetching directions ${result}`);
        }
      });
    }

    setDirections(finalDirectionResults);
  }, [pickUpLocation]);

  const routeKey = useMemo(
    () => [coordinateKey(pickUpLocation), ...orders.map(stop => coordinateKey(stop.dropOffLocation))].join("|"),
    [pickUpLocation, orders]
  );

  const getEstimate = useCallback(async () => {
    try {
      setLoading(true);

      const routeStops = orders.filter(stop => stop.dropOffLocation.coordinate.latitude !== "");
      const response = await getPublicEstimate({
        pickUpLocation,
        orders: routeStops.map((stop, index) => ({ ordinal: index, dropOffLocation: stop.dropOffLocation }))
      });

      const fees = response.data.orders || [];
      const feeByCoordinate = new Map(
        routeStops.map((stop, index) => [coordinateKey(stop.dropOffLocation), fees[index] ? fees[index].deliveryFee : 0])
      );
      setOrders(prevOrders => prevOrders.map(stop => ({
        ...stop,
        deliveryFee: feeByCoordinate.get(coordinateKey(stop.dropOffLocation)) || 0
      })));

      setShowDropOffInfoWindow(routeStops.map(() => false));
      getDirectionRoutes(routeStops);
      raiseError(false, "");
    } catch (e) {
      raiseError(true, e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, getDirectionRoutes, raiseError]);

  useEffect(() => {
    if (!hasPickUpCoordinate || orders.length === 0) return;
    getEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, hasPickUpCoordinate]);

  const estimatedTotal = orders.reduce((total, stop) => total + (stop.deliveryFee || 0), 0);

  /* ---------- details ---------- */

  const onPickUpDetailsChange = (unitNumber, pickUpContactNumber, pickUpPersonName) => {
    const formattedPhoneNumber = formatPhoneNumber(pickUpContactNumber);

    if (!formattedPhoneNumber) {
      return { field: "contactNumber", message: "Enter a valid sender's contact number, starting with the country code (eg. +60)." };
    }

    if (pickUpPersonName === "") {
      return { field: "personName", message: "Enter the sender's name." };
    }

    setPickUpForm(prevPickUpForm => ({
      ...prevPickUpForm,
      pickUpContactNumber: formattedPhoneNumber,
      pickUpPersonName,
      pickUpLocation: { ...prevPickUpForm.pickUpLocation, unitNumber }
    }));

    setEnterPickUpDetails(false);
    raiseError(false, "");
  }

  const onDropOffDetailsChange = (deliverDateTime, unitNumber, recipientPhone, recipientName, doorToDoor, isCodEnabled, codAmount) => {
    if (deliverDateTime === "") {
      return { field: "deliverDateTime", message: "Select the delivery date." };
    }

    if (new Date(deliverDateTime) - new Date() < 0) {
      return { field: "deliverDateTime", message: "Delivery date should not be in the past." };
    }

    if (pickUpForm.pickUpDateTime && new Date(deliverDateTime) - new Date(pickUpForm.pickUpDateTime) < 0) {
      return { field: "deliverDateTime", message: "Delivery date should be later than the pick-up date." };
    }

    const formattedPhoneNumber = formatPhoneNumber(recipientPhone);

    if (!formattedPhoneNumber) {
      return { field: "contactNumber", message: "Enter a valid recipient's contact number, starting with the country code (eg. +60)." };
    }

    if (recipientName === "") {
      return { field: "personName", message: "Enter the recipient's name." };
    }

    if (isCodEnabled && (codAmount === null || isNaN(codAmount) || codAmount <= 0)) {
      return { field: "codAmount", message: "Enter the COD amount to collect." };
    }

    const index = Number(editStopIndex);
    setOrders(prevOrders => prevOrders.map((stop, i) =>
      i === index
        ? {
          ...stop,
          recipientName,
          recipientPhone: formattedPhoneNumber,
          deliverDateTime,
          doorToDoor,
          isCodEnabled,
          codAmount: isCodEnabled ? (codAmount * 100) : 0,
          dropOffLocation: { ...stop.dropOffLocation, unitNumber }
        }
        : stop
    ));

    setEditStopIndex("");
    raiseError(false, "");
  }

  /* ---------- route edits ---------- */

  const moveStop = (oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;

    setOrders(prevOrders => {
      const next = [...prevOrders];
      next.splice(newIndex, 0, next.splice(oldIndex, 1)[0]);
      return next;
    });
    setEditStopIndex("");
  }

  const removeStop = (index) => {
    setOrders(prevOrders => prevOrders.filter((stop, i) => i !== index));
    setEditStopIndex("");
    if (orders.length === 1) setDirections([]);
  }

  /* ---------- confirm ---------- */

  const incompleteStopIndex = orders.findIndex(stop => !(stop.recipientName && stop.recipientPhone && stop.deliverDateTime));

  const gate = !pickUpLocation.address
    ? { title: "Add a pick-up location", hint: "Start by choosing where the parcel is collected from." }
    : !(pickUpForm.pickUpPersonName && pickUpForm.pickUpContactNumber)
      ? { title: "Add the sender's details", hint: "We need a name and contact number before we can price the delivery." }
      : orders.length === 0
        ? { title: "Add a drop-off location", hint: "Your estimate appears as soon as the first stop is costed." }
        : null;

  const canConfirm = !gate && Boolean(pickUpForm.pickUpDateTime) && incompleteStopIndex === -1 && !error;

  const openLogin = () => {
    saveGuestDraft(pickUpForm, orders);
    setShowLoginModal(true);
  }

  const handleConfirmOrder = () => {
    if (!canConfirm) return;
    openLogin();
  }

  const ctaHint = incompleteStopIndex !== -1
    ? `Stop ${incompleteStopIndex + 1} still needs a name and contact number.`
    : !gate && !pickUpForm.pickUpDateTime
      ? "Choose a pick-up time to continue."
      : "Log in or sign up to place this order. Your stops and details are kept.";

  const editingStop = editStopIndex !== "" && orders[editStopIndex];

  return (
    <>
      {loading && <LoadingOverlay />}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}

      <div className={styles.container}>
        <Topbar page="Place Order" />

        <div className={`${styles.poPanel} ${isCompact && sheetCollapsed ? styles.poPanelCollapsed : ""}`}>
          <div className={styles.poGrabBar} onClick={() => setSheetCollapsed(!sheetCollapsed)}>
            <i />
          </div>

          <div className={styles.poScroll} ref={panelScrollRef}>
            {
              error &&
              <Alert
                severity="error"
                style={{ marginBottom: 12, borderRadius: 10, textAlign: "left" }}
                onClose={() => raiseError(false, "")}>
                {errorMessage}
              </Alert>
            }

            {/* ---------- Vehicle ---------- */}
            <VehicleSelector
              value={pickUpForm.deliveryMethod}
              onChange={(deliveryMethod) => setPickUpForm(prevPickUpForm => ({ ...prevPickUpForm, deliveryMethod }))}
            />

            {/* ---------- Pick-up time ---------- */}
            <div className={styles.poSec}>
              <div className={styles.poSecHead}><h3>Pick-up time</h3></div>
              <div className={styles.poCard}>
                <div className={styles.poTimeRow}>
                  <img src={clockIcon} alt="" />
                  <DateTimeField
                    inputId="pickUpDateTime"
                    className={styles.poAddrInput}
                    value={pickUpForm.pickUpDateTime}
                    onChange={(date) => setPickUpForm(prevPickUpForm => ({ ...prevPickUpForm, pickUpDateTime: date }))}
                    minDate={new Date()}
                    minTime={setHours(setMinutes(new Date(), 0), 6)}
                    maxTime={setHours(setMinutes(new Date(), 0), 23)}
                    placeholder="Preferred pick-up time"
                  />
                </div>
              </div>
            </div>

            {/* ---------- Preferences ---------- */}
            <div className={styles.poSec}>
              <div className={styles.poSecHead}><h3>Preferences</h3></div>
              <div className={styles.poCard}>
                <div className={styles.poOptRow}>
                  <img src={halalIcon} alt="" />
                  <div className={styles.poOptTxt}>
                    <strong>Halal</strong>
                    <span>Match only riders with halal handling</span>
                  </div>
                  <div style={{ marginLeft: "auto" }}>
                    <TealSwitch
                      checked={pickUpForm.halal}
                      onChange={() => setPickUpForm(prevPickUpForm => ({ ...prevPickUpForm, halal: !prevPickUpForm.halal }))}
                      size="small"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ---------- Route ---------- */}
            <div className={styles.poSec}>
              <div className={styles.poSecHead}>
                <h3>Route</h3>
                <div className={styles.poSecTools}>
                  <InfoTooltip
                    title="Drag or renumber the drop-offs to find the cheapest order of stops."
                    placement="top"
                    arrow>
                    <div>
                      <i className="fas fa-info-circle" style={{ color: "#53d9e0", fontSize: 16 }} />
                    </div>
                  </InfoTooltip>
                </div>
              </div>

              <div className={styles.poCard}>
                {/* pick-up stop */}
                <div className={`${styles.poStop} ${enterPickUpDetails ? styles.poStopActive : ""}`}>
                  <div className={styles.poRail}>
                    <div className={`${styles.poPin} ${styles.poPinPickUp}`}>P</div>
                    <div className={styles.poThread} />
                  </div>

                  <div className={styles.poStopMain}>
                    <input
                      className={styles.poAddrInput}
                      id="pickUpAutocomplete"
                      name="pickUpAddress"
                      value={pickUpLocation.address}
                      onChange={(e) => setPickUpForm(prevPickUpForm => ({
                        ...prevPickUpForm,
                        pickUpLocation: { ...prevPickUpForm.pickUpLocation, address: e.target.value }
                      }))}
                      placeholder="Pick-up location"
                      autoComplete="off"
                    />

                    <div className={styles.poMeta} onClick={openPickUpDetails}>
                      {
                        pickUpForm.pickUpPersonName
                          ? <span className={styles.poMetaText}>
                            {pickUpForm.pickUpPersonName}&nbsp;&nbsp;·&nbsp;&nbsp;{pickUpForm.pickUpContactNumber}
                            {pickUpLocation.unitNumber ? `  ·  ${pickUpLocation.unitNumber}` : ""}
                          </span>
                          : <span className={styles.poMissing}>
                            <i className="fas fa-exclamation-circle" style={{ fontSize: 12 }} />
                            Add contact details
                          </span>
                      }
                      <StopSummaryTooltip rows={buildPickUpSummary(pickUpForm)} />
                    </div>
                  </div>

                  <div className={styles.poStopActs}>
                    <InfoTooltip title="Use my current location" placement="top" arrow>
                      <button className={styles.poIco} onClick={handleUseCurrentLocation}>
                        <img src={targetIcon} alt="current location" />
                      </button>
                    </InfoTooltip>
                    <InfoTooltip title="Log in to use your saved addresses" placement="top" arrow>
                      <button className={styles.poIco} onClick={openLogin}>
                        <img src={savedAddressIcon} alt="saved addresses" />
                      </button>
                    </InfoTooltip>
                  </div>
                </div>

                {/* drop-off stops */}
                {
                  orders.length > 0 &&
                  <SortableList
                    shouldCancelStart={(e) =>
                      Boolean(e.target.closest && e.target.closest(`input, button, .${styles.poMeta}`))
                    }
                    onSortEnd={({ oldIndex, newIndex }) => moveStop(oldIndex, newIndex)}
                    orders={orders}
                    editStopIndex={editStopIndex}
                    addStop={addStop}
                    onAddressChange={handleStopAddressChange}
                    onOpenDetails={(index) => { setEnterPickUpDetails(false); setEditStopIndex(index); }}
                    onRemove={removeStop}
                    onMove={moveStop}
                    onError={raiseError}
                  />
                }

                {/* new drop-off */}
                {
                  addStop &&
                  <div className={styles.poStop}>
                    <div className={styles.poRail}>
                      <div className={`${styles.poPin} ${styles.poPinDropOff}`}>{orders.length + 1}</div>
                    </div>

                    <div className={styles.poStopMain}>
                      <input
                        className={styles.poAddrInput}
                        id={`dropOffAutocomplete-${orders.length}`}
                        name="dropOffAddress"
                        placeholder="Drop-off location"
                        autoComplete="off"
                      />
                    </div>

                    <div className={styles.poStopActs}>
                      <InfoTooltip title="Remove this stop" placement="top" arrow>
                        <button className={styles.poIco} onClick={() => setAddStop(false)}>
                          <img src={closeIcon} alt="remove" />
                        </button>
                      </InfoTooltip>
                    </div>
                  </div>
                }

                <button className={styles.poAddStop} onClick={() => { setAddStop(true); setEditStopIndex(""); }}>
                  <img src={plusIcon} alt="" />
                  Add stop
                </button>
              </div>
            </div>
          </div>

          {/* ---------- Price footer ---------- */}
          <div className={styles.poFoot}>
            {
              gate
                ? <div className={styles.poGate}>
                  <img src={locationPinIcon} alt="" />
                  <div className={styles.poGateTxt}>
                    <strong>{gate.title}</strong>
                    <span>{gate.hint}</span>
                  </div>
                </div>
                : <>
                  <div className={styles.poLines}>
                    <div>
                      <span>Subtotal ({orders.length} {orders.length === 1 ? "stop" : "stops"})</span>
                      <span>RM{(estimatedTotal / 100).toFixed(2)}</span>
                    </div>
                  </div>

                  <div className={styles.poTotal}>
                    <p className={styles.poTotalLbl}>Estimated total</p>
                    <p className={styles.poTotalVal}>
                      <small>RM</small>{(estimatedTotal / 100).toFixed(2)}
                    </p>
                  </div>
                </>
            }

            <button className={styles.poCta} disabled={!canConfirm} onClick={handleConfirmOrder}>
              {
                canConfirm
                  ? `Confirm order · RM${(estimatedTotal / 100).toFixed(2)}`
                  : "Confirm order"
              }
            </button>

            <p className={`${styles.poCtaHint} ${incompleteStopIndex !== -1 ? styles.poCtaHintBlocked : ""}`}>
              {ctaHint}
            </p>
          </div>
        </div>

        {/* ---------- Map ---------- */}
        <div className={styles.map}>
          {
            enterPickUpDetails &&
            <StopDetailsPopover
              type="PICK_UP"
              stopKey="pick-up"
              ordinal="P"
              form={pickUpForm}
              onCancel={closeDetails}
              onSave={onPickUpDetailsChange}
            />
          }
          {
            editingStop &&
            <StopDetailsPopover
              type="DROP_OFF"
              stopKey={`stop-${editStopIndex}`}
              ordinal={Number(editStopIndex) + 1}
              form={{
                ...orders[editStopIndex],
                pickUpDateTime: pickUpForm.pickUpDateTime,
                codAmount: orders[editStopIndex].codAmount
                  ? (orders[editStopIndex].codAmount / 100).toFixed(2)
                  : 0
              }}
              onCancel={closeDetails}
              onSave={onDropOffDetailsChange}
            />
          }

          {
            directions.length > 0
              ? <PublicEstimateMap
                loadingElement={<div style={{ height: `100%` }} />}
                containerElement={<div style={{ height: "100%" }} />}
                mapElement={<div style={{ height: `100%` }} />}
                showPickUpInfoWindow={showPickUpInfoWindow}
                showDropOffInfoWindow={showDropOffInfoWindow}
                pickUpLocation={pickUpLocation}
                orders={orders}
                directions={directions}
                setShowPickUpInfoWindow={setShowPickUpInfoWindow}
                setShowDropOffInfoWindow={(idx, show) =>
                  setShowDropOffInfoWindow(orders.map((stop, i) => i === idx && show))
                }
              />
              : <SimpleMap
                loadingElement={<div style={{ height: `100%` }} />}
                containerElement={<div style={{ height: "100%" }} />}
                mapElement={<div style={{ height: `100%` }} />}
                center={mapFocus}
                zoom={SELECTED_ADDRESS_ZOOM}
                marker={mapFocus}
              />
          }
        </div>
      </div>
    </>
  )
}
